import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import axiosInstance from './axiosConfig';
import LoadingSpinner from './components/LoadingSpinner';
import './Financials.css';

// Register Chart.js components
ChartJS.register(
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend
);

const Financials = ({ clubId }) => {
    const [financialData, setFinancialData] = useState([]);
    const [einData, setEinData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchFinancialData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch main financial data
                const financialRes = await axiosInstance.get(`/financial-data/${clubId}/`);
                setFinancialData(financialRes.data);

                // Fetch EIN data
                const einRes = await axiosInstance.get(`/financial-ein/${clubId}/`);
                if (einRes.data.length > 0) {
                    setEinData(einRes.data[0]); // Assuming only one EIN record per club
                }
            } catch (err) {
                console.error('Error fetching financial data:', err);
                setError('Our research team has not captured the Financial Data for this club.');
            } finally {
                setLoading(false);
            }
        };

        if (clubId) fetchFinancialData();
    }, [clubId]);

    if (loading) return <LoadingSpinner />;

    // Show friendly message when no tax data is available
    if (error || !Array.isArray(financialData) || financialData.length === 0) {
        return (
            <div className="no-tax-data">

            </div>
        );
    }

    // Prepare data for charts - with safety checks
    const years = financialData.map(item => item.tax_prd_yr);
    const revenues = financialData.map(item => item.totrevenue);
    const assets = financialData.map(item => item.totassetsend);
    const expenses = financialData.map(item => item.totfuncexpns);
    const liabilities = financialData.map(item => item.totliabend);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
                position: 'top',
            },
        },
        layout: {
            padding: {
                bottom: 20,
            },
        },
        scales: {
            x: {
                title: {
                    display: false,
                    text: 'Year',
                },
            },
            y: {
                title: {
                    display: false,
                    text: 'Amount (in Million USD)',
                },
                ticks: {
                    callback: function (value) {
                        return `$${(value / 1e6).toFixed(1)}M`;
                    },
                },
            },
        },
    };

    const chartData = (label, data, color) => ({
        labels: years,
        datasets: [
            {
                label,
                data,
                borderColor: color,
                backgroundColor: `${color}33`, // Transparent fill
                tension: 0.4,
                fill: true,
            },
        ],
    });

    return (
        <div className="financials-container">
            <div className="course-info-container">
                <div className="course-header-container">
                    <h1 className="course-name">Tax Information</h1></div>
                <hr className="section-divider"/>
                <hr className="divider"/>

                {/* EIN Card Section */}
                {einData && (
                    <div className="course-info-2">
                        <p><strong>Source:</strong> IRS Form 990</p>
                        <p><strong>EIN Name:</strong> {einData.ein_name || 'Not Available'}</p>
                        <p><strong>EIN:</strong> {einData.ein || 'Not Available'}</p>
                    </div>
                )}
                 </div>
                {/* Financial Graphs */}
                <div className="financials-graphs">
                    <div className="graph-container">
                        <h3>Total Revenue</h3>
                        <Line
                            data={chartData('Total Revenue', revenues, '#004d40')}
                            options={chartOptions}
                            height={300}
                        />
                    </div>
                    <div className="graph-container">
                        <h3>Total Expenses</h3>
                        <Line
                            data={chartData('Total Expenses', expenses, '#004d40')}
                            options={chartOptions}
                            height={300}
                        />
                    </div>
                    <div className="graph-container">
                        <h3>Total Assets</h3>
                        <Line
                            data={chartData('Total Assets', assets, '#004d40')}
                            options={chartOptions}
                            height={300}
                        />
                    </div>
                    <div className="graph-container">
                        <h3>Total Liabilities</h3>
                        <Line
                            data={chartData('Total Liabilities', liabilities, '#004d40')}
                            options={chartOptions}
                            height={300}
                        />
                    </div>
                </div>
            </div>
            );
            };

            export default Financials;